import React from 'react';
import { Box, Typography, Container, Paper, Grid } from '@mui/material';
import { motion } from 'framer-motion';
import {
  ClipboardCheck,
  Warehouse,
  FlaskConical,
  Flame,
  CircleDollarSign,
  ChevronRight
} from 'lucide-react';
import { gradients } from '../theme';
import FadeIn from './FadeIn';

const steps = [
  {
    number: 1,
    title: 'Identification',
    description: 'Converter intake and accurate PGM identification.',
    icon: <ClipboardCheck size={32} />
  },
  {
    number: 2,
    title: 'Extraction',
    description: 'Professional decanning and catalyst material extraction.',
    icon: <Warehouse size={32} />
  },
  {
    number: 3,
    title: 'Analysis',
    description: 'XRF and chemical analysis to determine metal content.',
    icon: <FlaskConical size={32} />
  },
  {
    number: 4,
    title: 'Refining',
    description: 'High-purity precious metal refining and recovery.',
    icon: <Flame size={32} />
  },
  {
    number: 5,
    title: 'Payout',
    description: 'Market-linked pricing and transparent financial returns.',
    icon: <CircleDollarSign size={32} />
  },
];

const RecyclingProcess: React.FC = () => {
  return (
    <Box
      sx={{
        backgroundColor: 'black',
        py: { xs: 10, md: 15 },
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Dynamic Background Glows */}
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(0, 242, 255, 0.05) 0%, transparent 70%)',
          filter: 'blur(80px)',
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: '500px',
          height: '500px',
          background: 'radial-gradient(circle, rgba(0, 242, 255, 0.03) 0%, transparent 70%)',
          filter: 'blur(80px)',
          zIndex: 0,
        }}
      />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <FadeIn direction="up">
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <Typography
              variant="overline"
              sx={{ color: '#00f2ff', fontWeight: 600, letterSpacing: 2, mb: 2, display: 'block' }}
            >
              OUR WORKFLOW
            </Typography>
            <Typography
              variant="h2"
              sx={{
                ...gradients.primaryText,
                mb: 3,
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                letterSpacing: '-0.02em',
              }}
            >
              Precision Recycling Process
            </Typography>
            <Typography
              sx={{
                color: 'grey.400',
                fontSize: { xs: 18, md: 20 },
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              We follow a rigorous, tech-driven process to ensure every gram of precious metal is accounted for and fairly valued.
            </Typography>
          </Box>
        </FadeIn>

        <Box sx={{ position: 'relative' }}>
          {/* Connecting Line (Desktop) */}
          <Box
            sx={{
              display: { xs: 'none', lg: 'block' },
              position: 'absolute',
              top: '80px',
              left: '10%',
              right: '10%',
              height: '2px',
              background: 'linear-gradient(90deg, transparent 0%, rgba(0, 242, 255, 0.2) 20%, rgba(0, 242, 255, 0.2) 80%, transparent 100%)',
              zIndex: 0,
            }}
          />

          <Grid container spacing={3} justifyContent="center" sx={{ position: 'relative', zIndex: 1 }}>
            {steps.map((step, index) => (
              <Grid key={step.number} size={{ xs: 12, sm: 6, lg: 2.4 }}>
                <FadeIn delay={index * 0.1} fullWidth>
                  <Box
                    component={motion.div}
                    whileHover={{ y: -5 }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      px: 2,
                    }}
                  >
                    {/* Icon Container */}
                    <Box
                      sx={{
                        width: 160,
                        height: 160,
                        mb: 4,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {/* Step Number Badge */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: 'rgba(0, 242, 255, 0.1)',
                          backdropFilter: 'blur(5px)',
                          border: '1px solid rgba(0, 242, 255, 0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 2,
                        }}
                      >
                        <Typography sx={{ color: '#00f2ff', fontWeight: 800, fontSize: '0.9rem' }}>
                          0{step.number}
                        </Typography>
                      </Box>

                      {/* Main Circle */}
                      <Paper
                        sx={{
                          width: 120,
                          height: 120,
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255, 255, 255, 0.03)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'grey.300',
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          zIndex: 1,
                          '&:hover': {
                            borderColor: '#00f2ff',
                            color: '#00f2ff',
                            backgroundColor: 'rgba(0, 242, 255, 0.05)',
                            transform: 'scale(1.1)',
                            boxShadow: '0 0 40px rgba(0, 242, 255, 0.2)',
                          },
                        }}
                      >
                        {step.icon}
                      </Paper>

                      {/* Connector Arrow (Desktop) */}
                      {index < steps.length - 1 && (
                        <Box
                          sx={{
                            display: { xs: 'none', lg: 'flex' },
                            position: 'absolute',
                            right: '-30px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'rgba(0, 242, 255, 0.3)',
                          }}
                        >
                          <ChevronRight size={24} />
                        </Box>
                      )}
                    </Box>

                    {/* Content */}
                    <Box>
                      <Typography
                        variant="h5"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          mb: 1.5,
                          fontSize: '1.25rem',
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'grey.400',
                          fontSize: '0.95rem',
                          lineHeight: 1.6,
                          maxWidth: 240,
                          mx: 'auto',
                        }}
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  </Box>
                </FadeIn>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default RecyclingProcess;
